import brand from '@helpers/brand';
import { fluidAdjusted } from '@helpers/fluid';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { h4, paragraphMedium } from '@helpers/typography';
import { default as SvgBack, default as SvgChevron } from '@img/icons/chevron_right.svg';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled.nav `
  position: fixed;
  top: ${fluidAdjusted(100, 160)};
  left: 0;
  display: flex;
  flex-direction: column;
  background: var(--bgColor);
  color: var(--fgColor);
  height: calc(100vh - 120px);
  opacity: 0;
  overflow-y: auto;
  padding: 0 0 30px;
  pointer-events: none;
  transition: opacity 0.15s;
  width: 100%;
  z-index: 100;

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }

  &[data-m-active='true'] {
    opacity: 1;
    pointer-events: all;
  }
`;
const TopArea = styled.div `
  background-color: var(--backgroundColor);
  border-bottom: 1px solid var(--separatorColor);
  padding: ${fluidAdjusted(12, 36)} 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ActionWrapper = styled.div `
  grid-column: 3 / span 2;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${fluidAdjusted(18, 36)};

  @media ${from(Device.Tablet)} {
    grid-column: 4 / span 5;
  }

  @media ${from(Device.Desktop)} {
    grid-column: 5 / span 8;
  }

  > ${ButtonStyles.Button} {
    order: 3;
    white-space: nowrap;

    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }
`;
const CloseButton = styled.button `
  ${ButtonReset};

  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  color: var(--textColor);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  > svg {
    display: block;
    height: 16px;
    width: 16px;
  }

  ${ActionWrapper}[data-action-open='false'] & {
    display: none;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const ListArea = styled.div `
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const List = styled.ul `
  padding: 0;
  flex: 1 0 auto;
`;
const Item = styled.li ``;
const Arrow = styled(SvgChevron) `
  margin-left: auto;
  margin-right: 0;
  height: 15px;
`;
const Link = styled.a `
  display: flex;
  align-items: center;
  color: ${brand.secondary.red5};
  background-color: ${rgba(brand.white, 0.05)};
  font-weight: 900;
  font-size: 16px;
  letter-spacing: -0.01em;
  line-height: 1em;
  text-decoration: none;
  padding: 16px 20px;
  border-radius: 4px;
  transition: color 0.3s, background-color 0.3s;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    display: block;
    left: -18px;
    height: 24px;
    width: 4px;
    border-radius: 400000px;
    background-color: transparent;
  }

  &[data-active-page='true'] {
    color: ${brand.buff.buff4};
    background-color: ${brand.white};

    &:after {
      background-color: ${brand.white};
    }
  }

  svg {
    margin-right: 10px;
  }

  ${Arrow} {
    margin-right: 0;
  }

  @media (hover: hover) {
    &:focus,
    &:hover {
      color: ${brand.buff.buff4};
      background-color: ${brand.white};
      text-decoration: none;
    }
  }

  ${Item}:not(:first-child) & {
    [data-has-grandchildren='false'] & {
      font-weight: 300;
    }
  }
`;
const Text = styled.span `
  svg {
    margin-right: 5px;
  }

  svg,
  span {
    vertical-align: middle;
  }
`;
const BackSvg = styled(SvgBack) `
  margin-left: auto;
  height: 15px;
`;
const Header = styled.header `
  border-bottom: 1px solid ${brand.primary.grey};
`;
const Back = styled.button `
  ${h4};
  display: flex;
  align-items: center;
  background: none;
  border: 0;
  margin: 0;
  padding: 15px 20px;
  color: ${brand.secondary.red5};

  span {
    position: relative;
  }

  > svg {
    margin-right: 10px;
    transform: scaleX(-1);
  }
`;
const IntroContent = styled.p `
  ${paragraphMedium};
  font-size: ${fluidAdjusted(14, 20)};
  line-height: ${fluidAdjusted(20, 26)};
  padding: 0 20px;
`;
const Current = styled(Link) `
  color: ${brand.white};
  text-decoration: none;
  margin-bottom: 10px;
`;
const SmallList = styled(List) `
  padding: 5px 0;
`;
const SmallItem = styled(Item) ``;
const SmallLink = styled(Link) `
  color: ${brand.buff.buff4};
  font-weight: 400;
  font-size: 16px;
`;
const LogOutBtn = styled.button `
  display: flex;
  align-items: center;
  color: ${brand.buff.buff4};
  border: none;
  background-color: ${rgba(brand.white, 0.05)};
  font-weight: 400;
  font-size: 16px;
  letter-spacing: -0.01em;
  line-height: 1em;
  text-decoration: none;
  padding: 14px 10px;
  border-radius: 4px;
  transition: color 0.3s, background-color 0.3s;
  position: relative;
  width: 100%;

  &:after {
    content: '';
    position: absolute;
    display: block;
    left: -18px;
    height: 24px;
    width: 4px;
    border-radius: 400000px;
    background-color: transparent;
  }

  svg {
    margin-right: 10px;
  }

  @media (hover: hover) {
    &:focus,
    &:hover {
      color: ${brand.buff.buff2};
      background-color: ${brand.white};
      text-decoration: none;
    }
  }
`;
const Divider = styled.hr `
  display: block;
  border: 0;
  border-bottom: 1px solid ${brand.buff.buff4};
  width: 100%;
`;
const HomeLink = styled.a ``;
const NavLogo = styled.div ``;
const LogoMedia = styled.img `
  display: block;
  max-height: ${fluidAdjusted(40, 52)};
`;
const AccountBtnHolder = styled.div `
  grid-column: 12;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const AccountBtn = styled.a `
  background-color: transparent;
  display: block;
  border: 2px solid ${brand.white};
  border-radius: 9000000px;
  width: 40px;
  height: 40px;
  padding: 3px;
  text-decoration: none;
`;
const AccountBtnInner = styled.div `
  font-weight: 700;
  font-size: 14px;
  color: ${brand.buff.buff2};
  border-radius: 9000000px;
  width: 30px;
  height: 30px;
  background-color: ${brand.white};
  display: flex;
  justify-content: center;
  align-items: center;
`;
const MobileNavigationStyles = {
    AccountBtn,
    AccountBtnHolder,
    AccountBtnInner,
    Arrow,
    Back,
    BackSvg,
    CloseButton,
    Container,
    Current,
    Divider,
    Header,
    HomeLink,
    IntroContent,
    Item,
    Link,
    List,
    ListArea,
    LogoMedia,
    LogOutBtn,
    NavLogo,
    SmallItem,
    SmallLink,
    SmallList,
    Text,
    TopArea,
};
export default MobileNavigationStyles;
